import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { LCS_API_CONFIG, LcsApiConfig } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectQueryParams } from 'src/app/app.selectors';
import { LoginDialogComponent } from 'src/app/pages/login/dialog/login-dialog.component';
import { UserSessionStorageService } from 'src/app/session/user-session-storage.service';
import { ApplicationLogin } from '../models/application-login.interface';
import * as AuthenticationActions from '../state/authentication.actions';
import { getCurrentUser } from './authentication.selector';
@Injectable()
export class AuthenticationEffects {
   login = createEffect(() => {
      return this.actions.pipe(
         ofType(AuthenticationActions.login),
         exhaustMap(({ username, password }) => {
            return this.getLoginApiCall(username, password).pipe(
               map((response: HttpResponse<ApplicationLogin>) => {
                  const data: ApplicationLogin = response.body as ApplicationLogin;
                  return AuthenticationActions.loginSuccess({
                     loginResult: data,
                  });
               }),
               catchError(({ error }) => {
                  return of(AuthenticationActions.loginFailure(error));
               })
            );
         })
      );
   });

   loginSuccess = createEffect(
      () => {
         return this.actions.pipe(
            ofType(AuthenticationActions.loginSuccess),
            withLatestFrom(this.store.select(selectQueryParams)),
            tap(([action, queryParams]) => {
               this.userSessionStorage.setHasSession(true);

               if (queryParams.redirectUrl) {
                  this.router.navigateByUrl(queryParams.redirectUrl);
               } else {
                  this.router.navigate(['/']);
               }
            })
         );
      },
      { dispatch: false }
   );

   reAuthenticate = createEffect(() => {
      return this.actions.pipe(
         ofType(AuthenticationActions.reAuthenticate),
         exhaustMap(({ username, password }) => {
            return this.getLoginApiCall(username, password).pipe(
               map((response: HttpResponse<ApplicationLogin>) => {
                  const data: ApplicationLogin = response.body as ApplicationLogin;
                  return AuthenticationActions.reAuthenticationSuccess({
                     loginResult: data,
                  });
               }),
               catchError(({ error }) => {
                  return of(AuthenticationActions.reAuthenticationFailure(error));
               })
            );
         })
      );
   });

   reAuthenticationSuccess = createEffect(
      () => {
         return this.actions.pipe(
            ofType(AuthenticationActions.reAuthenticationSuccess),
            tap(() => {
               if (this.loginDialogId) {
                  this.closeDialog(this.loginDialogId);
               }
            })
         );
      },
      { dispatch: false }
   );

   logout$ = createEffect(() => {
      return this.actions.pipe(
         ofType(AuthenticationActions.logout),
         switchMap(() => {
            return from(this.router.navigate(['/logout'])).pipe(
               filter((routedSuccessfully: boolean) => {
                  return routedSuccessfully;
               }),
               map(() => {
                  return AuthenticationActions.apiLogout();
               })
            );
         })
      );
   });

   apiLogout = createEffect(() => {
      return this.actions.pipe(
         ofType(AuthenticationActions.apiLogout),
         switchMap(() => {
            return this.http.post<void>(this.logoutUrl, undefined).pipe(
               map(() => {
                  return AuthenticationActions.apiLogoutSuccess();
               }),
               catchError(({ error }) => {
                  return of(AuthenticationActions.apiLogoutFailure(error));
               })
            );
         })
      );
   });

   logoutSuccess = createEffect(
      () => {
         return this.actions.pipe(
            ofType(AuthenticationActions.apiLogoutSuccess, AuthenticationActions.reAuthenticationCancel),
            tap(() => {
               if (this.loginDialogId) {
                  this.closeDialog(this.loginDialogId);
               }
               this.userSessionStorage.removeHasSession();
               this.router.navigateByUrl('/login');
            })
         );
      },
      { dispatch: false }
   );

   reAuthenticationRequired$ = createEffect(
      () => {
         return this.actions.pipe(
            ofType(AuthenticationActions.reAuthenticationRequired),
            withLatestFrom(this.store.select(getCurrentUser)),
            exhaustMap(([action, currentUser]) => {
               if (currentUser == null) {
                  return EMPTY;
               }
               return this.openLoginDialog(currentUser.username);
            })
         );
      },
      { dispatch: false }
   );

   ssoLogin = createEffect(
      () => {
         return this.actions.pipe(
            ofType(AuthenticationActions.ssoLogin),
            tap((action) => {
               const abortController = new AbortController();
               let timeout: any = null;

               const handleSsoSuccessMessage = (message) => {
                  if (message.data.type === 'ssoLoginSuccess') {
                     this.store.dispatch(AuthenticationActions.ssoLoginSuccess({ fromDialog: action.fromDialog }));
                     abortController.abort();
                     window.removeEventListener('message', handleSsoSuccessMessage);
                     if (timeout) clearTimeout(timeout);
                  }
               };

               window.addEventListener('message', handleSsoSuccessMessage, { signal: abortController.signal });

               const width = Math.min(Math.max(window.innerWidth / 2, 300), window.innerWidth);
               const height = Math.min(Math.max(window.innerHeight / 2, 500), window.innerHeight);
               const top = (window.top?.outerHeight ?? 0) / 2 + (window.top?.screenY ?? 0) - height / 2;
               const left = (window.top?.outerWidth ?? 0) / 2 + (window.top?.screenX ?? 0) - width / 2;

               window.open(
                  this.ssoLoginUrl,
                  '_blank',
                  `popup=true,width=${width},height=${height},left=${left},top=${top}`
               );
               timeout = setTimeout(() => {
                  this.store.dispatch(AuthenticationActions.ssoLoginFailure());
                  abortController.abort();
                  window.removeEventListener('message', handleSsoSuccessMessage);
               }, 1000 * 60 * 5);
            })
         );
      },
      { dispatch: false }
   );

   ssoLoginInfo = createEffect(() => {
      return this.actions.pipe(
         ofType(AuthenticationActions.ssoLoginSuccess),
         switchMap((action) => {
            return this.getLoginInfoApiCall().pipe(
               map((response: HttpResponse<ApplicationLogin>) => {
                  const data: ApplicationLogin = response.body as ApplicationLogin;

                  if (action.fromDialog) {
                     return AuthenticationActions.reAuthenticationSuccess({ loginResult: data });
                  }
                  return AuthenticationActions.loginSuccess({
                     loginResult: data,
                     fromDialog: action.fromDialog,
                  });
               }),
               catchError((error) => {
                  return of(AuthenticationActions.loginFailure(error));
               })
            );
         })
      );
   });

   ssoLoginSuccess = createEffect(
      () =>
         this.router.events.pipe(
            filter((ev) => {
               if (ev instanceof NavigationEnd && ev.url.toLowerCase() === '/login-info') {
                  return true;
               }
               return false;
            }),
            map(() => {
               window.opener.parent.postMessage({ type: 'ssoLoginSuccess' });
               window.close();
            })
         ),
      { dispatch: false }
   );

   ssoLoginTimeout = createEffect(() =>
      this.router.events.pipe(
         filter((ev) => {
            if (ev instanceof NavigationEnd && ev.url.toLowerCase() === '/sso-login-timeout') {
               return true;
            }
            return false;
         }),
         map(() => {
            return AuthenticationActions.ssoLoginTimeout();
         })
      )
   );

   ssoLoginFailure = createEffect(() =>
      this.router.events.pipe(
         filter((ev) => {
            if (ev instanceof NavigationEnd && ev.url.toLowerCase() === '/sso-login-failure') {
               return true;
            }
            return false;
         }),
         map(() => {
            return AuthenticationActions.ssoLoginFailure();
         })
      )
   );

   private loginUrl: string;

   private logoutUrl: string;

   private loginDialogId: string | null;

   private ssoLoginUrl: string;

   private ssoLoginInfoUrl: string;

   constructor(
      protected actions: Actions,
      private http: HttpClient,
      @Inject(LCS_API_CONFIG) apiConfig: LcsApiConfig,
      private router: Router,
      private store: Store<AppState>,
      private userSessionStorage: UserSessionStorageService,
      private dialog: MatDialog,
      private snackbar: MatSnackBar
   ) {
      if (!apiConfig || !apiConfig.baseUrl) {
         throw new Error("Invalid API configuration. 'baseUrl' is required.");
      }
      this.loginUrl = apiConfig.baseUrl + '/authentication/login';
      this.logoutUrl = apiConfig.baseUrl + '/authentication/logout';
      this.ssoLoginUrl = apiConfig.baseUrl + '/Saml2/InitiateSingleSignOn';
      this.ssoLoginInfoUrl = apiConfig.baseUrl + '/session/status';
   }

   openLoginDialog(username: string) {
      const dialogRef = this.dialog.open<LoginDialogComponent>(LoginDialogComponent, {
         closeOnNavigation: false,
         data: { username },
         disableClose: true,
         panelClass: 'dialog-md',
      });
      this.loginDialogId = dialogRef.id;
      return dialogRef.afterClosed();
   }

   private getLoginApiCall(username: string, password: string) {
      return this.http.post<ApplicationLogin>(
         this.loginUrl,
         // eslint-disable-next-line @typescript-eslint/naming-convention
         { Password: password, Username: username },
         { observe: 'response' }
      );
   }

   private getLoginInfoApiCall() {
      return this.http.get<ApplicationLogin>(
         this.ssoLoginInfoUrl,
         // eslint-disable-next-line @typescript-eslint/naming-convention
         { observe: 'response', withCredentials: true }
      );
   }

   private closeDialog(dialogId: string) {
      const dialogRef = this.dialog.getDialogById(dialogId);
      this.loginDialogId = null;
      if (dialogRef) {
         dialogRef.close();
      }
   }
}
